import {
  Dialog,
  DialogPanel,
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Popover,
  PopoverButton,
  PopoverGroup,
  PopoverPanel,
} from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { useSolutionsData } from "../model/data/solutionsData";
import { SvgIcon } from "./icons/SvgIcon";
import { Button } from "./ui/button";
import { RoutesEnum } from "../model/enum/RoutesEnum";
import { ImagesEnum } from "../model/enum/ImagesEnum";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { useTranslation } from "react-i18next";
import { getApiUrl } from "../utils/api";

export function Navigation() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [hasBlogPosts, setHasBlogPosts] = useState(false);
  const { solutions } = useSolutionsData();
  const { t } = useTranslation();
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 0);
    };
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    fetch(getApiUrl("/backend/api/blog-posts.php?page=1&pageSize=1"))
      .then((r) => r.json())
      .then((d) => setHasBlogPosts(d.total > 0))
      .catch(() => {});
  }, []);

  return (
    <header className="sticky top-0 z-50 pt-4 px-10 max-w-[1440px] mx-auto">
      <nav
        aria-label="Global"
        className={`mx-auto ${isScrolled ? "bg-gradient-to-r from-brand-gradientDarkBlue to-brand-gradientLightBlue text-white/70" : "bg-white text-black/70"} rounded-2xl flex items-center justify-between py-6 mb-4 px-4 ${isScrolled ? "shadow-md" : ""} transition-all duration-300 ease-in-out`}
      >
        <div className="flex lg:flex-1">
          <Link to={RoutesEnum.HOME} className="-m-1.5 p-1.5">
            {isScrolled ? (
              <img
                src={ImagesEnum.LOGO_WHITE}
                className="h-12 w-auto"
                alt="Logo"
              />
            ) : (
              <img src={ImagesEnum.LOGO} className="h-12 w-auto" alt="Logo" />
            )}
          </Link>
        </div>
        <div className="flex lg:hidden">
          <Button
            type="button"
            onClick={() => setMobileMenuOpen(true)}
            className=" inline-flex items-center justify-center rounded-md p-0 text-black"
          >
            <span className="sr-only">{t("navigation:menu")}</span>
            <Bars3Icon
              aria-hidden="true"
              className={` ${isScrolled ? "text-white" : "text-black"} size-6`}
            />
          </Button>
        </div>
        <PopoverGroup className="hidden lg:flex lg:gap-x-12 items-center">
          <Popover className="relative">
            {({ close }) => (
              <>
                <PopoverButton className="flex items-center gap-x-1 text-sm focus:outline-none focus:ring-0">
                  {t("navigation:solutions")}
                  <ChevronDownIcon
                    aria-hidden="true"
                    className="size-5 flex-none"
                  />
                </PopoverButton>

                <PopoverPanel
                  transition
                  className="absolute left-1/2 z-10 mt-3 w-screen max-w-3xl -translate-x-1/2 overflow-hidden rounded-2xl bg-white shadow-lg outline outline-1 outline-gray-900/5 transition data-[closed]:translate-y-1 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in"
                >
                  <div className="p-4 grid grid-cols-2 grid-flow-row">
                    {solutions.map((item) => {
                      const name =
                        "nameKey" in item
                          ? t(item.nameKey)
                          : (item as any).name;
                      const description =
                        "descriptionKey" in item
                          ? t(item.descriptionKey)
                          : (item as any).description;
                      return (
                        <div
                          key={name}
                          className="group relative flex items-center gap-x-6 rounded-lg p-2 text-sm/6"
                        >
                          <div className="flex size-11 flex-none items-center justify-center rounded-lg bg-brand-homepageGradientLight group-hover:bg-brand-secondary">
                            <SvgIcon
                              className="group-hover:fill-white group-hover:stroke-white fill-brand-secondary stroke-brand-secondary"
                              icon={item.icon}
                            />
                          </div>
                          <div className="flex-auto">
                            <Link
                              to={item.href}
                              className="block font-bold text-brand-darkGray group-hover:text-brand-secondary"
                              onClick={() => close()}
                            >
                              {name}
                              <span className="absolute inset-0" />
                            </Link>
                            <p className="mt-1 text-brand-lightGray">
                              {description}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </PopoverPanel>
              </>
            )}
          </Popover>

          <Link className="text-sm" to={RoutesEnum.PROJECTS}>
            {t("navigation:projects")}
          </Link>
          {hasBlogPosts && (
            <Link className="text-sm" to={RoutesEnum.BLOG}>
              {t("navigation:blog")}
            </Link>
          )}
          <Link className="text-sm" to={RoutesEnum.ABOUT}>
            {t("navigation:about")}
          </Link>
          <Link className="text-sm" to={RoutesEnum.CONTACT}>
            <Button
              className={`m-0 ${isScrolled ? "bg-white text-brand-primary hover:bg-brand-lightButtonHover" : "bg-brand-primary text-white hover:bg-brand-primary/90"} font-normal transition-all duration-300 ease-in-out`}
            >
              {t("common:contactUs")}
            </Button>
          </Link>
          <LanguageSwitcher />
        </PopoverGroup>
      </nav>
      <Dialog
        open={mobileMenuOpen}
        onClose={setMobileMenuOpen}
        className="lg:hidden"
      >
        <div className="fixed inset-0 z-50" />
        <DialogPanel className="fixed inset-y-0 px-14 pt-10 right-0 z-50 w-full overflow-y-auto bg-white p-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
          <div className="flex items-center justify-between">
            <Link to={RoutesEnum.HOME} className="-m-1.5 p-1.5 sm:hidden">
              <img src={ImagesEnum.LOGO} className="h-12 w-auto" alt="Logo" />
            </Link>
            <Button
              type="button"
              onClick={() => setMobileMenuOpen(false)}
              className="inline-flex items-center justify-center rounded-md p-2.5 text-black"
            >
              <span className="sr-only">{t("common:close")}</span>
              <XMarkIcon aria-hidden="true" className="size-6" />
            </Button>
          </div>
          <div className="mt-6 flow-root">
            <div className="-my-6 divide-y divide-gray-500/10">
              <div className="space-y-2 py-6">
                <Disclosure as="div" className="">
                  <DisclosureButton className="group flex w-full items-center justify-between rounded-lg py-2 pr-3.5 text-base/7 font-semibold text-gray-900 hover:bg-gray-50 dark:hover:bg-white/5">
                    {t("navigation:solutions")}
                    <ChevronDownIcon
                      aria-hidden="true"
                      className="size-5 flex-none group-data-[open]:rotate-180"
                    />
                  </DisclosureButton>
                  <DisclosurePanel className="mt-2 space-y-2">
                    {[...solutions].map((item) => {
                      const name =
                        "nameKey" in item
                          ? t(item.nameKey)
                          : (item as any).name;
                      return (
                        <DisclosureButton
                          key={name}
                          as="a"
                          href={item.href}
                          className="block rounded-lg py-2 pl-6 pr-3 text-sm/7 font-semibold text-gray-900 hover:bg-gray-50 dark:hover:bg-white/5"
                        >
                          {name}
                        </DisclosureButton>
                      );
                    })}
                  </DisclosurePanel>
                </Disclosure>
                <Link
                  className="block rounded-lg py-2 text-base/7 font-semibold text-gray-900 hover:bg-gray-50 dark:hover:bg-white/5"
                  to={RoutesEnum.PROJECTS}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t("navigation:projects")}
                </Link>
                {hasBlogPosts && (
                  <Link
                    className="block rounded-lg py-2 text-base/7 font-semibold text-gray-900 hover:bg-gray-50 dark:hover:bg-white/5"
                    to={RoutesEnum.BLOG}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t("navigation:blog")}
                  </Link>
                )}
                <Link
                  className="block rounded-lg py-2 text-base/7 font-semibold text-gray-900 hover:bg-gray-50 dark:hover:bg-white/5"
                  to={RoutesEnum.ABOUT}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t("navigation:about")}
                </Link>
                <div className="py-2">
                  <LanguageSwitcher />
                </div>
                <Link
                  className="block rounded-lg py-2 text-base/7 font-semibold text-gray-900 hover:bg-gray-50 dark:hover:bg-white/5"
                  to={RoutesEnum.CONTACT}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Button className="text-base/7 bg-brand-primary text-white hover:bg-brand-primary/90 font-normal">
                    {t("common:contactUs")}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </DialogPanel>
      </Dialog>
    </header>
  );
}
